import React from 'react';
import { colorForKind, SpanKindIcon } from 'shared/components/spans/KindTag';

/**
 * One span-kind vocabulary, shared by the spans table and the run drawer.
 *
 * There were two maps, and they disagreed. spanCells.js coloured `tool` amber
 * and `agent` blue; AgentRunDetailPage coloured `tool` blue and `agent` a
 * hardcoded #b45309 — so the SAME span changed colour depending on whether you
 * were looking at the table or the drawer you opened from it. They also used
 * different labels (RET vs Retrieval) and different treatments: the table drew
 * a tinted pill, the drawer a solid fill with white text.
 *
 * The table's assignment won, and colours and glyphs now live in one map,
 * KIND_COLORS in shared/components/spans/KindTag, which the trace tree reads too.
 */

export const SPAN_KIND_LABEL = {
  llm: 'LLM',
  tool: 'TOOL',
  agent: 'AGENT',
  retrieval: 'RETRIEVAL',
  generic: 'SPAN',
};

export function spanKindLabel(kind) {
  return SPAN_KIND_LABEL[kind] || (kind ? String(kind).toUpperCase() : SPAN_KIND_LABEL.generic);
}

export function spanKindColor(kind) {
  return colorForKind(kind).color;
}

/**
 * The span-kind badge — the icon alone.
 *
 * This used to spell the kind out (`AGENT`, `TOOL`, `LLM`). In the spans table
 * that word sat in front of every id, repeating down the whole column and
 * pushing the name it belonged to off to the right. The trace tree has always
 * marked kinds with an icon, so the table now uses the same one: the column
 * scans as a strip of marks, and the two views finally agree.
 *
 * The label survives as the tooltip and the accessible name, so nothing is
 * lost for anyone who doesn't recognise a glyph yet.
 */
export function KindPill({ kind }) {
  const label = spanKindLabel(kind);
  return (
    <span
      className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center"
      style={{ color: spanKindColor(kind) }}
      title={label}
      aria-label={label}
      role="img"
    >
      <SpanKindIcon kind={kind} size={13} color="currentColor" />
    </span>
  );
}

/**
 * The span kind under a "Kind" label — the icon AND the word.
 *
 * KindPill is icon-only because in the spans table the word repeated down the
 * whole column in front of every id. A meta card is the opposite case: the
 * field is labelled "Kind" and holds exactly one value, so an icon on its own
 * says nothing a reader can name. Opening a span from the Spans tab, the
 * pop-up's Kind field was a wrench with no word beside it — you could see the
 * span was something, not that it was a tool.
 */
export function KindLabel({ kind }) {
  const label = spanKindLabel(kind);
  return (
    <span className="inline-flex items-center gap-1.5" style={{ color: spanKindColor(kind) }}>
      <SpanKindIcon kind={kind} size={13} color="currentColor" />
      <span className="text-[11px] font-semibold tracking-wide">{label}</span>
    </span>
  );
}
